import React from "react";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import Select from "react-select";

const calculatePercentage = (correct, total) =>
  total === 0 ? "0%" : `${((correct / total) * 100).toFixed(2)}%`;

const calculateScore = (data) =>
  ["direct", "fiftyFifty", "oneEliminate"].map((type) => ({
    score: `${data[type].correct}/${data[type].total}`,
    percent: calculatePercentage(data[type].correct, data[type].total).replace(
      "%",
      ""
    ),
    color:
      type === "direct"
        ? "#f97316"
        : type === "fiftyFifty"
        ? "#ec4899"
        : "#a855f7",
  }));

const categories = ["100%Sure", "logicPlay", "intuitionHit", "blindFire"];

const Analysis = ({ analysisData, subjectData }) => {
  const [filter, setFilter] = React.useState();
  const category = Object.keys(subjectData).map((item) => ({
    label: item,
    value: item,
  }));
  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "0.375rem",
      borderColor: "#D1D5DB",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#124699",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#124699" : "white",
      color: state.isSelected ? "white" : "#4B5563",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: state.isSelected ? "#124699" : "#e8effa",
        color: state.isSelected ? "white" : "#4B5563",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#4B5563",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#4B5563",
      "&:hover": {
        color: "#124699",
      },
    }),
  };
  return (
    <div className="w-full bg-white text-black p-4 rounded-md">
      <div>
        <div className="grid grid-cols-4 text-center font-semibold text-gray-700 border-b pb-2">
          <p></p>
          <p>Direct</p>
          <p>50-50</p>
          <p>1-Opt Eliminate</p>
        </div>

        {categories.map((category) => {
          const totalAttempts =
            analysisData[category]?.direct.total +
            analysisData[category]?.fiftyFifty.total +
            analysisData[category]?.oneEliminate.total;

          const correctAttempts =
            analysisData[category]?.direct.correct +
            analysisData[category]?.fiftyFifty.correct +
            analysisData[category]?.oneEliminate.correct;

          return (
            <div key={category} className="border-b py-3">
              <div className="grid grid-cols-4 text-center items-center">
                <div className="flex flex-col justify-center items-center">
                  <p className="font-medium">{category}</p>
                  <div className="w-12 my-1 bg-blue-500 rounded-full h-2"></div>
                  <p className="font-medium">
                    {calculatePercentage(correctAttempts, totalAttempts)}
                  </p>
                </div>
                {calculateScore(analysisData[category])?.map((attempt, idx) => (
                  <div key={idx} className="flex flex-col items-center">
                    <div className="relative w-16 h-16">
                      <CircularProgressbar
                        value={attempt?.percent}
                        text={attempt?.score}
                        styles={buildStyles({
                          textColor: "#000",
                          pathColor: attempt.color,
                          trailColor: "#e5e7eb",
                        })}
                      />
                    </div>
                    <p className="text-sm text-gray-600">{attempt?.percent}%</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="border-b py-3">
          <div className="grid grid-cols-4 text-center items-center">
            <div className="flex flex-col justify-center items-center">
              <p className="font-medium">Fear Skip:</p>
              <p className="font-medium text-gray-600">
                {Object.keys(analysisData?.fearDriverSkip)
                  .reduce((acc, key) => {
                   
                    const { correct, total } = analysisData?.fearDriverSkip[key];
                    return acc + correct * 2 + (total - correct) * -0.67;
                  }, 0)
                  .toFixed(2)}
              </p>
              <p className="font-medium text-sm">Marks Chance</p>
            </div>
            {["direct", "fiftyFifty", "oneEliminate"].map((type, idx) => (
              <div key={idx} className="flex flex-col items-center font-medium">
                <p>
                  {analysisData?.fearDriverSkip[type].correct}/
                  {analysisData?.fearDriverSkip[type].total}
                </p>
                <p className="text-gray-600">
                  {(
                    analysisData?.fearDriverSkip[type].correct * 2 +
                    (analysisData?.fearDriverSkip[type].total -
                      analysisData?.fearDriverSkip[type].correct) *
                      -0.67
                  ).toFixed(2)}
                </p>
                <p className="text-sm">Marks Chance</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-center">
        {category && (
          <div className="flex items-center gap-2 sm:w-72 w-full mt-8">
            <p>Filter:</p>
            <Select
              className="w-full"
              value={category?.find((option) => option.value === filter)}
              onChange={(e) => setFilter(e.value)}
              options={category}
              styles={customStyles}
              placeholder="Select filter"
            />
          </div>
        )}
      </div>
      {filter && subjectData && (
        <div>
          <div className="grid grid-cols-4 text-center font-semibold text-gray-700 border-b pb-2 mt-4">
            <p></p>
            <p>Direct</p>
            <p>50-50</p>
            <p>1-Opt Eliminate</p>
          </div>

          {Object.keys(subjectData)?.map((category) => {
            if (category === filter) {
              const categoryData = JSON.parse(
                JSON.stringify(subjectData[category])
              );
              return (
                <div>
                  <div className="grid grid-cols-4 text-center items-center border-b py-3">
                    <div className="flex flex-col justify-center items-center">
                      <p className="font-medium">100%Sure</p>
                      <div className="w-12 my-1 bg-blue-500 rounded-full h-2"></div>
                      <p className="font-medium">
                        {calculatePercentage(
                          (categoryData["100%Sure"]?.direct?.correct || 0) +
                            (categoryData["100%Sure"]?.fiftyFifty?.correct ||
                              0) +
                            (categoryData["100%Sure"]?.oneEliminate?.correct ||
                              0),
                          (categoryData["100%Sure"]?.direct?.total || 0) +
                            (categoryData["100%Sure"]?.fiftyFifty?.total || 0) +
                            (categoryData["100%Sure"]?.oneEliminate?.total || 0)
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData["100%Sure"]?.direct?.correct || 0,
                            categoryData["100%Sure"]?.direct?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData["100%Sure"]?.direct?.correct || 0
                          }/${categoryData["100%Sure"]?.direct?.total || 0}`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#f97316",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData["100%Sure"]?.direct?.correct || 0,
                          categoryData["100%Sure"]?.direct?.total || 0
                        ).replace("%", "")}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData["100%Sure"]?.fiftyFifty?.correct || 0,
                            categoryData["100%Sure"]?.fiftyFifty?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData["100%Sure"]?.fiftyFifty?.correct || 0
                          }/${
                            categoryData["100%Sure"]?.fiftyFifty?.total || 0
                          }`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#ec4899",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData["100%Sure"]?.fiftyFifty?.correct || 0,
                          categoryData["100%Sure"]?.fiftyFifty?.total || 0
                        ).replace("%", "")}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData["100%Sure"]?.oneEliminate?.correct ||
                              0,
                            categoryData["100%Sure"]?.oneEliminate?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData["100%Sure"]?.oneEliminate?.correct || 0
                          }/${
                            categoryData["100%Sure"]?.oneEliminate?.total || 0
                          }`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#a855f7",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData["100%Sure"]?.oneEliminate?.correct || 0,
                          categoryData["100%Sure"]?.oneEliminate?.total || 0
                        ).replace("%", "")}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 text-center items-center border-b py-3">
                    <div className="flex flex-col justify-center items-center">
                      <p className="font-medium">logicPlay</p>
                      <div className="w-12 my-1 bg-blue-500 rounded-full h-2"></div>
                      <p className="font-medium">
                        {calculatePercentage(
                          (categoryData?.logicPlay?.direct?.correct || 0) +
                            (categoryData?.logicPlay?.fiftyFifty?.correct || 0) +
                            (categoryData?.logicPlay?.oneEliminate?.correct ||
                              0),
                          (categoryData?.logicPlay?.direct?.total || 0) +
                            (categoryData?.logicPlay?.fiftyFifty?.total || 0) +
                            (categoryData?.logicPlay?.oneEliminate?.total || 0)
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.logicPlay?.direct?.correct || 0,
                            categoryData?.logicPlay?.direct?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.logicPlay?.direct?.correct || 0
                          }/${categoryData?.logicPlay?.direct?.total || 0}`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#f97316",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.logicPlay?.direct?.correct || 0,
                          categoryData?.logicPlay?.direct?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.logicPlay?.fiftyFifty?.correct || 0,
                            categoryData?.logicPlay?.fiftyFifty?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.logicPlay?.fiftyFifty?.correct || 0
                          }/${categoryData?.logicPlay?.fiftyFifty?.total || 0}`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#ec4899",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.logicPlay?.fiftyFifty?.correct || 0,
                          categoryData?.logicPlay?.fiftyFifty?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.logicPlay?.oneEliminate?.correct || 0,
                            categoryData?.logicPlay?.oneEliminate?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.logicPlay?.oneEliminate?.correct || 0
                          }/${
                            categoryData?.logicPlay?.oneEliminate?.total || 0
                          }`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#a855f7",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.logicPlay?.oneEliminate?.correct || 0,
                          categoryData?.logicPlay?.oneEliminate?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 text-center items-center border-b py-3">
                    <div className="flex flex-col justify-center items-center">
                      <p className="font-medium">intuitionHit</p>
                      <div className="w-12 my-1 bg-blue-500 rounded-full h-2"></div>
                      <p className="font-medium">
                        {calculatePercentage(
                          (categoryData?.intuitionHit?.direct?.correct || 0) +
                            (categoryData?.intuitionHit?.fiftyFifty?.correct ||
                              0) +
                            (categoryData?.intuitionHit?.oneEliminate?.correct ||
                              0),
                          (categoryData?.intuitionHit?.direct?.total || 0) +
                            (categoryData?.intuitionHit?.fiftyFifty?.total ||
                              0) +
                            (categoryData?.intuitionHit?.oneEliminate?.total ||
                              0)
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.intuitionHit?.direct?.correct || 0,
                            categoryData?.intuitionHit?.direct?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.intuitionHit?.direct?.correct || 0
                          }/${categoryData?.intuitionHit?.direct?.total || 0}`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#f97316",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.intuitionHit?.direct?.correct || 0,
                          categoryData?.intuitionHit?.direct?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.intuitionHit?.fiftyFifty?.correct || 0,
                            categoryData?.intuitionHit?.fiftyFifty?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.intuitionHit?.fiftyFifty?.correct || 0
                          }/${
                            categoryData?.intuitionHit?.fiftyFifty?.total || 0
                          }`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#ec4899",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.intuitionHit?.fiftyFifty?.correct || 0,
                          categoryData?.intuitionHit?.fiftyFifty?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.intuitionHit?.oneEliminate?.correct ||
                              0,
                            categoryData?.intuitionHit?.oneEliminate?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.intuitionHit?.oneEliminate?.correct ||
                            0
                          }/${
                            categoryData?.intuitionHit?.oneEliminate?.total || 0
                          }`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#a855f7",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.intuitionHit?.oneEliminate?.correct || 0,
                          categoryData?.intuitionHit?.oneEliminate?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 text-center items-center border-b py-3">
                    <div className="flex flex-col justify-center items-center">
                      <p className="font-medium">blindFire</p>
                      <div className="w-12 my-1 bg-blue-500 rounded-full h-2"></div>
                      <p className="font-medium">
                        {calculatePercentage(
                          (categoryData?.blindFire?.direct?.correct || 0) +
                            (categoryData?.blindFire?.fiftyFifty?.correct || 0) +
                            (categoryData?.blindFire?.oneEliminate?.correct ||
                              0),
                          (categoryData?.blindFire?.direct?.total || 0) +
                            (categoryData?.blindFire?.fiftyFifty?.total || 0) +
                            (categoryData?.blindFire?.oneEliminate?.total || 0)
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.blindFire?.direct?.correct || 0,
                            categoryData?.blindFire?.direct?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.blindFire?.direct?.correct || 0
                          }/${categoryData?.blindFire?.direct?.total || 0}`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#f97316",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.blindFire?.direct?.correct || 0,
                          categoryData?.blindFire?.direct?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.blindFire?.fiftyFifty?.correct || 0,
                            categoryData?.blindFire?.fiftyFifty?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.blindFire?.fiftyFifty?.correct || 0
                          }/${categoryData?.blindFire?.fiftyFifty?.total || 0}`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#ec4899",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.blindFire?.fiftyFifty?.correct || 0,
                          categoryData?.blindFire?.fiftyFifty?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <CircularProgressbar
                          value={calculatePercentage(
                            categoryData?.blindFire?.oneEliminate?.correct || 0,
                            categoryData?.blindFire?.oneEliminate?.total || 0
                          ).replace("%", "")}
                          text={`${
                            categoryData?.blindFire?.oneEliminate?.correct || 0
                          }/${
                            categoryData?.blindFire?.oneEliminate?.total || 0
                          }`}
                          styles={buildStyles({
                            textColor: "#000",
                            pathColor: "#a855f7",
                            trailColor: "#e5e7eb",
                          })}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {calculatePercentage(
                          categoryData?.blindFire?.oneEliminate?.correct || 0,
                          categoryData?.blindFire?.oneEliminate?.total || 0
                        ).replace("%", "")}
                        %
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 text-center items-center">
                    <div className="flex flex-col justify-center items-center">
                      <p className="font-medium">Fear Skip:</p>
                      <p className="font-medium text-gray-600">
                        {categoryData?.fearDriverSkip
                          ? Object.keys(categoryData?.fearDriverSkip)
                              .reduce((acc, key) => {
                                const { correct = 0, total = 0 } =
                                  categoryData?.fearDriverSkip[key] || {};
                                return (
                                  acc + correct * 2 + (total - correct) * -0.67
                                );
                              }, 0)
                              .toFixed(2)
                          : "0.00"} 
                      </p>
                      <p className="font-medium text-sm">Marks Chance</p>
                    </div>

                    {["direct", "fiftyFifty", "oneEliminate"].map(
                      (type, idx) => (
                        <div
                          key={idx}
                          className="flex flex-col items-center font-medium"
                        >
                          <p>
                            {categoryData?.fearDriverSkip?.[type]?.correct || 0}
                            /{categoryData?.fearDriverSkip?.[type]?.total || 0}
                          </p>
                          <p className="text-gray-600">
                            {(
                              (categoryData?.fearDriverSkip?.[type]?.correct ||
                                0) *
                                2 +
                              ((categoryData?.fearDriverSkip?.[type]?.total ||
                                0) -
                                  (categoryData?.fearDriverSkip?.[type]
                                    ?.correct || 0)) *
                                -0.67
                            ).toFixed(2)}
                          </p>
                          <p className="text-sm">Marks Chance</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>
      )}
    </div>
  );
};

export default Analysis;
